import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Nav, Tab, Alert, Spinner, Button } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';

// Importar componentes de cada sección
import RolesSection from '../components/config/RolesSection';
import PermissionsMatrix from '../components/config/PermissionsMatrix';
import SystemSettings from '../components/config/SystemSettings';
import MessageTemplates from '../components/config/MessageTemplates';
import BrandingSettings from '../components/config/BrandingSettings';

// URL base del backend
const API_BASE = process.env.REACT_APP_NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PRODUCTION : 'http://localhost:3001';

const ConfiguracionPage = () => {
  // =====================================================
  // ESTADO
  // =====================================================
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('roles');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // =====================================================
  // CARGAR RESUMEN AL INICIAR
  // =====================================================
  useEffect(() => {
    cargarResumen();
  }, []);

  const cargarResumen = async () => {
    try {
      setLoading(true);
      const correo = localStorage.getItem('correo');
      
      const response = await fetch(`${API_BASE}/api/config/resumen`, {
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResumen(data.data);
        setError(null);
      } else {
        setError(data.error || 'Error al cargar resumen');
      }
    } catch (err) {
      setError('Error de conexión al cargar configuración');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // MOSTRAR MENSAJE DE ÉXITO
  // =====================================================
  const mostrarExito = (mensaje) => {
    setSuccessMessage(mensaje);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // =====================================================
  // ESTILOS
  // =====================================================
  const mainContentStyle = {
    marginLeft: sidebarOpen ? '250px' : '70px',
    transition: 'margin-left 0.3s ease-in-out',
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    padding: '20px'
  };

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================
  return (
    <div style={{ display: 'flex' }}>
      <Sidebar onToggle={setSidebarOpen} />
      
      <div style={mainContentStyle}>
        <Container fluid>
          {/* Header */}
          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="mb-1">
                    <i className="bi bi-gear-fill text-primary me-2"></i>
                    Configuración del Sistema
                  </h2>
                  <p className="text-muted mb-0">Administra roles, permisos y parámetros</p>
                </div>
                <Button 
                  variant="outline-secondary" 
                  onClick={cargarResumen}
                  disabled={loading}
                >
                  <i className={`bi bi-arrow-clockwise me-2 ${loading ? 'spin' : ''}`}></i>
                  Actualizar
                </Button>
              </div>
            </Col>
          </Row>

          {/* Mensaje de éxito */}
          {successMessage && (
            <Alert variant="success" dismissible onClose={() => setSuccessMessage(null)}>
              <i className="bi bi-check-circle me-2"></i>
              {successMessage}
            </Alert>
          )}

          {/* Mensaje de error */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {/* Resumen Cards */}
          {resumen && (
            <Row className="mb-4">
              <Col md={3}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="d-flex align-items-center">
                    <div className="bg-primary bg-opacity-10 rounded p-3 me-3">
                      <i className="bi bi-people-fill text-primary fs-4"></i>
                    </div>
                    <div>
                      <h3 className="mb-0">{resumen.totalRoles || 0}</h3>
                      <small className="text-muted">Roles</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="d-flex align-items-center">
                    <div className="bg-success bg-opacity-10 rounded p-3 me-3">
                      <i className="bi bi-person-fill text-success fs-4"></i>
                    </div>
                    <div>
                      <h3 className="mb-0">{resumen.totalUsuarios || 0}</h3>
                      <small className="text-muted">Usuarios</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="d-flex align-items-center">
                    <div className="bg-info bg-opacity-10 rounded p-3 me-3">
                      <i className="bi bi-envelope-fill text-info fs-4"></i>
                    </div>
                    <div>
                      <h3 className="mb-0">{resumen.totalPlantillas || 0}</h3>
                      <small className="text-muted">Plantillas</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="border-0 shadow-sm">
                  <Card.Body className="d-flex align-items-center">
                    <div className="bg-warning bg-opacity-10 rounded p-3 me-3">
                      <i className="bi bi-clock-history text-warning fs-4"></i>
                    </div>
                    <div>
                      <small className="text-dark fw-bold">
                        {resumen.ultimaModificacion 
                          ? new Date(resumen.ultimaModificacion).toLocaleDateString('es-CO')
                          : 'N/A'
                        }
                      </small>
                      <br />
                      <small className="text-muted">Última modificación</small>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Contenido Principal con Tabs */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white">
              <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
                <Nav.Item>
                  <Nav.Link eventKey="roles">
                    <i className="bi bi-people me-2"></i>
                    Roles
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="permisos">
                    <i className="bi bi-shield-check me-2"></i>
                    Matriz de Permisos
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="sistema">
                    <i className="bi bi-sliders me-2"></i>
                    Sistema
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="mensajes">
                    <i className="bi bi-chat-dots me-2"></i>
                    Plantillas
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="personalizacion">
                    <i className="bi bi-palette me-2"></i>
                    Personalización
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body style={{ minHeight: '400px' }}>
              {loading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
                  <Spinner animation="border" variant="primary" />
                  <span className="ms-3">Cargando...</span>
                </div>
              ) : (
                <>
                  {activeTab === 'roles' && <RolesSection onSuccess={mostrarExito} />}
                  {activeTab === 'permisos' && <PermissionsMatrix onSuccess={mostrarExito} />}
                  {activeTab === 'sistema' && <SystemSettings onSuccess={mostrarExito} />}
                  {activeTab === 'mensajes' && <MessageTemplates onSuccess={mostrarExito} />}
                  {activeTab === 'personalizacion' && <BrandingSettings onSuccess={mostrarExito} />}
                </>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* CSS para animación de spin */}
      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ConfiguracionPage;
